import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Route, Sparkles, Save, Share2, Clock, MapPin, RefreshCw, CalendarPlus, Navigation, Shuffle, Lock, Unlock, Car, MessageSquare, Image as ImageIcon } from "lucide-react";
import html2canvas from "html2canvas";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/constants";
import { CATEGORY_COLORS } from "@/components/CategoryIcon";

const DURATIONS = [
  { key: "evening", label: "An Evening" },
  { key: "half_day", label: "Half Day" },
  { key: "full_day", label: "Full Day" },
];
const GROUPS = [
  { key: "family", label: "Family" },
  { key: "couple", label: "Couple" },
  { key: "solo", label: "Solo" },
  { key: "equestrian", label: "Equestrian" },
];

function milesAway(origin, place) {
  if (!origin || !place?.lat || !place?.lng) return null;
  const R = 3958.8, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(place.lat - origin.lat), dLng = toRad(place.lng - origin.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(origin.lat)) * Math.cos(toRad(place.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(s)) * 10) / 10;
}

function driveInfo(prev, cur) {
  const mi = milesAway(prev, cur);
  if (mi == null || mi > 120) return null; // guard bad/missing coords
  return { miles: mi, mins: Math.max(2, Math.round((mi / 32) * 60)) }; // ~32 mph local avg
}

function icsEscape(s) {
  return String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");
}
function icsStamp(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}T${p(d.getHours())}${p(d.getMinutes())}00`;
}
function downloadICS(items, title, startTime = "10:00") {
  const base = new Date();
  base.setDate(base.getDate() + 1); // schedule for tomorrow
  const [sh, sm] = String(startTime).split(":").map(Number);
  base.setHours(sh || 10, sm || 0, 0, 0);
  const now = new Date();
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//CSRAGo//Day Plan//EN", "CALSCALE:GREGORIAN"];
  items.forEach((it, i) => {
    const start = new Date(base); start.setMinutes(start.getMinutes() + i * 120); // 2h blocks
    const end = new Date(start); end.setMinutes(end.getMinutes() + 90);
    lines.push(
      "BEGIN:VEVENT",
      `UID:csrago-${base.getTime()}-${i}@csrago.com`,
      `DTSTAMP:${icsStamp(now)}`,
      `DTSTART:${icsStamp(start)}`,
      `DTEND:${icsStamp(end)}`,
      `SUMMARY:${icsEscape(it.name)}`,
      it.address ? `LOCATION:${icsEscape(it.address)}` : "",
      it.note ? `DESCRIPTION:${icsEscape(it.note)}` : "",
      "END:VEVENT"
    );
  });
  lines.push("END:VCALENDAR");
  const blob = new Blob([lines.filter(Boolean).join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(title || "csrago-day-plan").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Itinerary() {
  const { user } = useAuth();
  const [duration, setDuration] = useState("half_day");
  const [group, setGroup] = useState("couple");
  const [interests, setInterests] = useState([]);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(null);
  const [coords, setCoords] = useState(null);
  const [startTime, setStartTime] = useState("10:00");
  const [locked, setLocked] = useState(() => new Set());
  const [restored, setRestored] = useState(false);

  const planTitle = `${DURATIONS.find((d) => d.key === duration)?.label || ""} · ${GROUPS.find((g) => g.key === group)?.label || ""}`;

  const toggleLock = (i) =>
    setLocked((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }, []);

  // Restore a plan saved on this device (works without an account)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("csrago_day_plan");
      if (!raw) return;
      const s = JSON.parse(raw);
      if (s.items?.length) {
        setItems(s.items);
        if (s.duration) setDuration(s.duration);
        if (s.group) setGroup(s.group);
        if (Array.isArray(s.interests)) setInterests(s.interests);
        if (s.startTime) setStartTime(s.startTime);
        setRestored(true);
      }
    } catch { /* ignore corrupt storage */ }
  }, []);

  // Auto-save the current plan to this device
  useEffect(() => {
    if (items?.length) {
      localStorage.setItem("csrago_day_plan", JSON.stringify({ items, duration, group, interests, startTime }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, duration, group, interests, startTime]);

  const clearSaved = () => {
    localStorage.removeItem("csrago_day_plan");
    setItems(null);
    setRestored(false);
    toast.success("Cleared the plan saved on this device.");
  };

  const toggleInterest = (c) =>
    setInterests((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const generate = async () => {
    setLoading(true);
    setSaved(null);
    setLocked(new Set());
    try {
      const { data } = await api.post("/itineraries/generate", {
        duration, group_type: group, interests,
        ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
      });
      setItems(data);
      if (!data.length) toast.info("No matches — try different interests.");
    } finally {
      setLoading(false);
    }
  };

  const reshuffle = async () => {
    if (!items?.length) return;
    setLoading(true);
    setSaved(null);
    try {
      const next = [...items];
      for (let i = 0; i < next.length; i++) {
        if (locked.has(i)) continue;
        const it = next[i];
        try {
          const { data } = await api.post("/itineraries/swap", {
            time_slot: it.time_slot,
            category: it.category,
            exclude_ids: next.map((x) => x?.place_id).filter(Boolean),
            ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
          });
          next[i] = data;
        } catch { /* keep existing stop if no alternative */ }
      }
      setItems(next);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!user) return toast.error("Sign in to save your plan");
    const title = `${DURATIONS.find((d) => d.key === duration).label} · ${GROUPS.find((g) => g.key === group).label}`;
    const { data } = await api.post("/itineraries", { title, items, duration, group_type: group, interests });
    setSaved(data);
    toast.success("Itinerary saved!");
  };

  const share = () => {
    if (!saved) return toast.error("Save your plan first to get a share link");
    const url = `${window.location.origin}/i/${saved.share_id}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied!");
  };

  const addToCalendar = () => {
    if (!items?.length) return;
    const title = `${DURATIONS.find((d) => d.key === duration).label} · ${GROUPS.find((g) => g.key === group).label}`;
    downloadICS(items, title, startTime);
    toast.success("Calendar file downloaded — open it to add your day.");
  };

  const textPlan = () => {
    if (!items?.length) return;
    const title = `${DURATIONS.find((d) => d.key === duration).label} · ${GROUPS.find((g) => g.key === group).label}`;
    const lines = items.map((it, i) => `${i + 1}. ${it.time_slot ? it.time_slot + " — " : ""}${it.name}${it.address ? ` (${it.address})` : ""}`);
    const body = `My CSRAGo day plan (${title}):\n${lines.join("\n")}\n\nBuilt with CSRAGo · csrago.com`;
    window.location.href = `sms:?&body=${encodeURIComponent(body)}`;
  };

  const sharePlanImage = async () => {
    const el = document.getElementById("share-card");
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: "#f7f1e6", useCORS: true });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "csrago-day-plan.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My CSRAGo day plan", text: "My day around the CSRA — plan yours at csrago.com" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "csrago-day-plan.png";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Plan image downloaded — share it anywhere!");
      }
    } catch {
      toast.error("Couldn't create the image. Please try again.");
    }
  };

  const openInMaps = () => {
    const pts = (items || []).filter((x) => x.lat && x.lng);
    if (!pts.length) return toast.error("These stops don't have map locations yet.");
    const origin = coords ? `${coords.lat},${coords.lng}` : `${pts[0].lat},${pts[0].lng}`;
    const startIdx = coords ? 0 : 1;
    const dest = `${pts[pts.length - 1].lat},${pts[pts.length - 1].lng}`;
    const waypoints = pts.slice(startIdx, pts.length - 1).map((p) => `${p.lat},${p.lng}`).join("|");
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`;
    if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
    window.open(url, "_blank", "noopener");
  };

  const swapStop = async (i) => {
    const it = items[i];
    try {
      const { data } = await api.post("/itineraries/swap", {
        time_slot: it.time_slot,
        category: it.category,
        exclude_ids: items.map((x) => x.place_id).filter(Boolean),
        ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
      });
      setItems((prev) => prev.map((x, idx) => (idx === i ? data : x)));
      toast.success(`Swapped for ${data.name}`);
    } catch {
      toast.info("No other nearby options for this stop.");
    }
  };

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Plan</span>
        <h1 className="flex items-center gap-2 font-serif text-3xl font-bold md:text-4xl"><Route className="text-primary" /> Day Plan Builder</h1>
        <p className="mt-1 text-muted-foreground">Tell us the vibe — we'll craft a route through the CSRA.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="mb-2 text-sm font-semibold">How long?</p>
          <div className="mb-5 flex gap-2">
            {DURATIONS.map((d) => (
              <button key={d.key} onClick={() => setDuration(d.key)} data-testid={`duration-${d.key}`}
                className={`flex-1 rounded-full px-3 py-2 text-sm font-medium ${duration === d.key ? "bg-primary text-primary-foreground" : "border border-border"}`}>{d.label}</button>
            ))}
          </div>

          <p className="mb-2 text-sm font-semibold">Who's going?</p>
          <div className="mb-5 grid grid-cols-2 gap-2">
            {GROUPS.map((g) => (
              <button key={g.key} onClick={() => setGroup(g.key)} data-testid={`group-${g.key}`}
                className={`rounded-full px-3 py-2 text-sm font-medium ${group === g.key ? "bg-secondary text-secondary-foreground" : "border border-border"}`}>{g.label}</button>
            ))}
          </div>

          <p className="mb-2 text-sm font-semibold">Interests</p>
          <div className="mb-5 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => toggleInterest(c)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium ${interests.includes(c) ? "text-white" : "border border-border"}`}
                style={interests.includes(c) ? { backgroundColor: CATEGORY_COLORS[c] } : {}}>{c}</button>
            ))}
          </div>

          <p className="mb-2 text-sm font-semibold">Start time</p>
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="start-time-input"
            className="mb-5 w-full rounded-full border border-border bg-card px-4 py-2 text-sm outline-none focus:border-primary" />

          <button onClick={generate} disabled={loading} data-testid="generate-itinerary-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-60">
            <Sparkles size={16} /> {loading ? "Building…" : "Generate my day"}
          </button>
        </div>

        <div>
          {items === null ? (
            <div className="flex h-full min-h-[300px] items-center justify-center rounded-2xl border border-dashed border-border text-center text-muted-foreground">
              Your custom day plan will appear here.
            </div>
          ) : items.length === 0 ? (
            <div className="flex h-full min-h-[300px] items-center justify-center rounded-2xl border border-dashed border-border text-muted-foreground">
              No spots matched — try broadening your interests.
            </div>
          ) : (
            <div>
              {restored && (
                <p className="mb-3 flex flex-wrap items-center gap-2 rounded-xl bg-secondary/10 px-3 py-2 text-xs text-muted-foreground" data-testid="restored-banner">
                  <Save size={13} /> Restored the plan saved on this device.
                  <button onClick={clearSaved} data-testid="clear-saved-btn" className="font-semibold text-primary underline">Clear</button>
                </p>
              )}
              <div className="mb-4 flex flex-wrap gap-2">
                <button onClick={reshuffle} disabled={loading} data-testid="shuffle-itinerary-btn" className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
                  <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Shuffle
                </button>
                <button onClick={openInMaps} data-testid="maps-itinerary-btn" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold">
                  <Navigation size={15} /> Open in Maps
                </button>
                <button onClick={addToCalendar} data-testid="calendar-itinerary-btn" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold">
                  <CalendarPlus size={15} /> Add to Calendar
                </button>
                <button onClick={save} data-testid="save-itinerary-btn" className="flex items-center gap-2 rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground">
                  <Save size={15} /> Save
                </button>
                <button onClick={share} data-testid="share-itinerary-btn" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold">
                  <Share2 size={15} /> Share
                </button>
                <button onClick={textPlan} data-testid="text-itinerary-btn" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold">
                  <MessageSquare size={15} /> Text plan
                </button>
                <button onClick={sharePlanImage} data-testid="share-image-itinerary-btn" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold">
                  <ImageIcon size={15} /> Share image
                </button>
              </div>
              <ol className="relative space-y-4 border-l-2 border-dashed border-border pl-6">
                {items.map((it, i) => {
                  const drive = i > 0 ? driveInfo(items[i - 1], it) : null;
                  const isLocked = locked.has(i);
                  return (
                  <li key={it.place_id || it.slug || i} className="relative" data-testid={`itinerary-item-${i}`}>
                    {drive && (
                      <p className="mb-2 ml-1 flex items-center gap-1 text-[11px] font-medium text-muted-foreground" data-testid={`drive-time-${i}`}>
                        <Car size={12} /> ~{drive.mins} min drive · {drive.miles} mi
                      </p>
                    )}
                    <div className="relative">
                      <span className="absolute -left-[31px] top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                      <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5">
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleLock(i); }} data-testid={`lock-stop-${i}`}
                          title={isLocked ? "Locked — Shuffle will keep this stop" : "Lock this stop"}
                          className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold backdrop-blur-sm transition-transform hover:scale-105 ${isLocked ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card/95 text-foreground hover:border-primary hover:text-primary"}`}>
                          {isLocked ? <Lock size={12} /> : <Unlock size={12} />} {isLocked ? "Locked" : "Lock"}
                        </button>
                        {!isLocked && (
                          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); swapStop(i); }} data-testid={`swap-stop-${i}`}
                            title="Swap this stop for another nearby option"
                            className="flex items-center gap-1 rounded-full border border-border bg-card/95 px-2.5 py-1 text-[11px] font-semibold text-foreground backdrop-blur-sm transition-transform hover:scale-105 hover:border-primary hover:text-primary">
                            <Shuffle size={12} /> Swap
                          </button>
                        )}
                      </div>
                      <Link to={it.slug ? `/place/${it.slug}` : it.place_id ? `/place/${it.place_id}` : "#"} className="card-hover flex gap-4 rounded-2xl border border-border bg-card p-3 pr-32">
                        {it.cover_image && <img src={it.cover_image} alt="" loading="lazy" decoding="async" className="h-20 w-20 flex-shrink-0 rounded-xl object-cover" />}
                        <div>
                          <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-primary"><Clock size={12} /> {it.time_slot}</span>
                          <h3 className="font-serif text-lg font-bold">{it.name}</h3>
                          <p className="text-sm text-muted-foreground">{it.note}</p>
                          {it.address && <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={11} /> {it.address}</p>}
                          {coords && milesAway(coords, it) != null && (
                            <span data-testid={`itinerary-distance-${i}`} className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                              {milesAway(coords, it)} mi away
                            </span>
                          )}
                        </div>
                      </Link>
                    </div>
                  </li>
                  );
                })}
              </ol>

              {/* Hidden branded card captured for "Share image" */}
              <div id="share-card" data-testid="share-card" style={{ position: "fixed", left: -99999, top: 0, width: 1080, padding: 64, background: "#f7f1e6", color: "#2b2119", fontFamily: "'Outfit', sans-serif" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 28 }}>
                  <img src="/icon-mark.png" alt="" crossOrigin="anonymous" width={72} height={72} style={{ borderRadius: "50%" }} />
                  <div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#c0492b", letterSpacing: 2, textTransform: "uppercase" }}>CSRAGo</div>
                    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 46, fontWeight: 800, lineHeight: 1 }}>My Day in the CSRA</div>
                  </div>
                </div>
                <div style={{ fontSize: 24, color: "#6b5d4a", marginBottom: 16 }}>{planTitle}</div>
                <div>
                  {items.map((it, i) => (
                    <div key={it.place_id || it.slug || i} style={{ display: "flex", gap: 20, padding: "18px 0", borderTop: "2px dashed #d9cdb8", alignItems: "center" }}>
                      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#c0492b", color: "#fff", fontWeight: 800, fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
                      {it.cover_image && (
                        <img src={(it.cover_image || "").replace(/^https?:\/\/[^/]+\/api\/files\//, "/api/files/")} alt="" crossOrigin="anonymous"
                          style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 16, flexShrink: 0 }} />
                      )}
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: "#c0492b", textTransform: "uppercase", letterSpacing: 1 }}>{it.time_slot}</div>
                        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 34, fontWeight: 700, lineHeight: 1.1 }}>{it.name}</div>
                        {it.address && <div style={{ fontSize: 20, color: "#6b5d4a", marginTop: 4 }}>{it.address}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 36, textAlign: "center", fontSize: 24, fontWeight: 700, color: "#c0492b" }}>Plan your CSRA day at csrago.com</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
